import {
  PublicClientApplication,
  AccountInfo,
  InteractionRequiredAuthError,
} from "@azure/msal-browser";
import {
  AuthConfig,
  AuthSource,
  B_TO_C_POLICIES,
  BToCPolicy,
  Environment,
  getAuthConfig,
  getScopesByConfig,
} from "@granular/fabric3-definitions";
import { getEnvironment } from "../../../helpers/environment";
import z from "zod";

export const MSAL_KNOWN_ACCOUNT_KEY = "sso-spa-known-account";

export const getAuthEnv = (): Environment | "beta" => {
  /**
   * TODO: this centralized entrypoint means we can replace the env here with "beta" if needed. We
   * haven't configured that yet.
   */
  return getEnvironment();
};

const KnownAccountSchema = z.object({
  authority: z.string(),
  account: z.object({
    homeAccountId: z.string(),
    environment: z.string(),
    tenantId: z.string(),
    localAccountId: z.string(),
    username: z.string(),
  }),
});

export type InitializedAccount = {
  authApp: PublicClientApplication;
  authSource: AuthSource;
  bToCPolicy: BToCPolicy | undefined;
  authConfig: AuthConfig;
  account: AccountInfo;
};

/**
 * Initializes a PublicClientApplication from the given config.
 * @param authConfig - Auth config to build the app with.
 * @param bToCPolicy - Optional B2C policy to use for the authority. Do not set for non-B2C configs.
 * @returns A PublicClientApplication
 *
 * Note: You may see DOM warnings about missing autocomplete attributes
 * on the Azure B2C login page. This is expected as the B2C page is
 * hosted by Microsoft and cannot be modified without custom page templates.
 */
export const makeAuthApp = async (
  authConfig: AuthConfig,
): Promise<PublicClientApplication> => {
  return (await PublicClientApplication.createPublicClientApplication({
    auth: {
      clientId: authConfig.clientId,
      authority: authConfig.authority,
      /** redirect to the origin of the current page by default */
      redirectUri: globalThis.location.origin,
      postLogoutRedirectUri: globalThis.location.origin,
      /**
       * when we redirect back from Microsoft, don't try to navigate to the URL
       * we came from. This way, we can land on the root without MSAL sending us
       * back to /login.
       */
      navigateToLoginRequestUrl: false,
      // For B2C, we need to specify knownAuthorities to prevent endpoint discovery errors
      ...(authConfig.knownAuthorities && {
        knownAuthorities: authConfig.knownAuthorities,
      }),
    },
    cache: {
      cacheLocation: "localStorage" as const,
    },
  })) as PublicClientApplication;
};

/**
 * Tries to initialize an account of the given type from the MSAL cache.
 *
 * @param accountSource - "b2b", "b2c", "vylor_b2b", the type of tenant to initialize for
 * @param bToCPolicy - Optional B2C policy to use when initializing a B2C config. Do not set for non-B2C configs.
 * @returns a PublicClientApplication, the active account, and the account source. Undefined if no account could be initialized.
 */
const tryInitAccount = async (
  redirectHash: string,
  accountSource: AuthSource,
  bToCPolicy: BToCPolicy | undefined = undefined,
  cacheOnly = true,
): Promise<InitializedAccount | undefined> => {
  const authConfig = getAuthConfig(getAuthEnv(), accountSource, bToCPolicy);
  const authApp = await makeAuthApp(authConfig);

  // This will pull any data from a redirect, if there is one
  const redirectResponse = await authApp.handleRedirectPromise(redirectHash);
  if (redirectResponse) {
    console.debug(
      "[MSAL] Intercepted MSAL redirect for:",
      redirectResponse.account.username,
    );

    // Remember the user's account IDs, so that we can use them to detect a remote session in auto-identify.
    // This shouldn't have anything sensitive, just IDs.
    localStorage.setItem(
      MSAL_KNOWN_ACCOUNT_KEY,
      JSON.stringify(
        // This parse will strip out all the values we don't want to store, and validate the shape.
        // Auth shouldn't fail because of this though, just session detection. `safeParse` will
        // return an error object on failure, which will cause a future read to fail.
        KnownAccountSchema.safeParse({
          authority: authConfig.authority,
          account: redirectResponse.account,
        }).data,
      ),
    );
    authApp.setActiveAccount(redirectResponse.account);
  }

  const account = authApp.getActiveAccount();
  if (account) {
    console.debug(
      `[MSAL] Found active account for ${accountSource}: ${account.username}`,
    );
    return {
      authApp: authApp,
      authSource: accountSource,
      bToCPolicy: bToCPolicy ?? undefined,
      authConfig: authConfig,
      account: account,
    };
  }

  if (!cacheOnly) {
    try {
      const parsedKnownAccount = KnownAccountSchema.safeParse(
        JSON.parse(localStorage.getItem(MSAL_KNOWN_ACCOUNT_KEY) ?? "{}"),
      );
      if (!parsedKnownAccount.success) return undefined;
      // This account was for a different authority, don't even try silent sso.
      if (parsedKnownAccount.data.authority !== authConfig.authority)
        return undefined;
      const knownAccount = parsedKnownAccount.data;
      const scopes = getScopesByConfig(authConfig);
      // This can fail if the user's browser blocks third-party cookies
      const sso = await authApp.ssoSilent({
        account: knownAccount.account,
        authority: authConfig.authority,
        scopes: scopes,
      });
      if (sso?.account) {
        authApp.setActiveAccount(sso.account);
        return {
          authApp: authApp,
          authSource: accountSource,
          bToCPolicy: bToCPolicy ?? undefined,
          authConfig: authConfig,
          account: sso.account,
        };
      }
    } catch (error) {
      // Only log unexpected errors
      if (!(error instanceof InteractionRequiredAuthError)) {
        console.error(
          `[MSAL] unexpected error in Silent SSO for ${accountSource}${bToCPolicy ? ` (${bToCPolicy})` : ""}:`,
          error,
        );
      }
      // If we return undefined here, the caller can try interactive login
      return undefined;
    }
  }

  return undefined;
};

/**
 * Tries to initialize both a b2b app and a b2c app from the MSAL cache.
 * Prefers b2b.
 * @returns The initialized account, or undefined if no account could be identified
 */
export const identifyAccount = async (
  cacheOnly = true,
): Promise<InitializedAccount | undefined> => {
  // Kick off all promises at once, ordered by precedence
  console.debug("[MSAL] Identifying account...");
  const hash = globalThis.location.hash;
  const accountPromises = [
    tryInitAccount(hash, "vylor_b2b", undefined, cacheOnly),
    tryInitAccount(hash, "b2b", undefined, cacheOnly),
    tryInitAccount(hash, "b2c", B_TO_C_POLICIES.SIGN_IN, cacheOnly),
    /** This allows us to accept tokens from the b2c invite redemption policy */
    tryInitAccount(hash, "b2c", B_TO_C_POLICIES.REDEEM, cacheOnly),
    /**
     * ROPC (api call with username and pwd) should never be used to sign a real
     * user in, because it means we would at some point be handling raw user
     * creds in our system.
     *
     * However our playwright tests use ROPC to quicky auth test accounts.  Here
     * we enable the SPA to recognize cached ROPC sessions, which is totally
     * fine to do in any env.
     */
    tryInitAccount(hash, "b2c", B_TO_C_POLICIES.ROPC, cacheOnly),
  ];

  // In precedence order, return the first one that works
  for (const ap of accountPromises) {
    const account = await ap;
    if (account) {
      console.debug(
        `[MSAL] Found cached account for ${account.account.homeAccountId}`,
      );
      return account;
    }
  }

  return undefined;
};

/**
 * Clears all MSAL accounts from B2B, B2C, and Vylor B2B caches.
 * This forces the user to select an account on the next login attempt.
 * Use this when implementing "Sign in with different account" functionality.
 */
export const clearAllAccounts = async (): Promise<void> => {
  console.debug("[MSAL] Clearing all cached accounts...");

  const authSources: AuthSource[] = ["b2b", "b2c", "vylor_b2b"];

  for (const source of authSources) {
    try {
      const authConfig = getAuthConfig(getAuthEnv(), source);
      const authApp = await makeAuthApp(authConfig);

      // Get all accounts and clear the active account
      const accounts = authApp.getAllAccounts();
      authApp.setActiveAccount(null);

      console.debug(
        `[MSAL] Cleared ${accounts.length} account(s) for ${source}`,
      );
    } catch (error) {
      console.warn(`[MSAL] Failed to clear accounts for ${source}:`, error);
    }
  }

  // Clear any cached tokens from localStorage as well
  localStorage.removeItem(MSAL_KNOWN_ACCOUNT_KEY);
};

/**
 * Clears any in-progress MSAL interactions for the given auth config.  This is useful to avoid
 * "interaction_in_progress" errors when the user has followed an auth link, and tried to revisit a
 * page without completing the login process.
 */
export const clearInProgressInteraction = async (
  authConfig: AuthConfig,
): Promise<void> => {
  try {
    const authApp = await makeAuthApp(authConfig);
    await authApp.handleRedirectPromise();
  } catch (error) {
    console.warn(
      `[MSAL] Failed to clear in-progress interaction for ${authConfig.authority}:`,
      error,
    );
  }
};
